import { useMemo } from 'react';
import type { AppConfig } from '../types';

const DEFAULT_TITLE = 'OpenGov Public Service Platform';

/**
 * Finds the registered app whose basePath matches the pathname.
 *
 * @param pathname - The current pathname (e.g., "/reporting/dashboard")
 * @param appRegistry - The app registry with resolved configs
 * @returns The matching app, or undefined when no app matches
 */
export function findAppForPath(
  pathname: string,
  appRegistry?: readonly AppConfig[]
): AppConfig | undefined {
  if (!appRegistry) {
    return undefined;
  }

  // Filter out apps with empty or undefined basePath
  const validApps = appRegistry.filter((app) => app.basePath && app.basePath.length > 0);

  // Sort by basePath length descending (longest first) to handle overlapping paths
  // This ensures /data_manager matches before /data
  const sortedApps = [...validApps].sort((a, b) => b.basePath.length - a.basePath.length);

  // Find first app where pathname matches the basePath
  // Special handling for root path "/" - only match exactly
  return sortedApps.find((app) => {
    if (app.basePath === '/') {
      return pathname === '/';
    }
    return pathname.startsWith(app.basePath);
  });
}

/**
 * Pure utility function to get page title from pathname.
 * Exported for testing purposes.
 *
 * @param pathname - The current pathname (e.g., "/reporting/dashboard")
 * @param appRegistry - The app registry with resolved configs
 * @returns The matching app's displayName or default title
 */
export function getPageTitleFromPath(pathname: string, appRegistry?: readonly AppConfig[]): string {
  return findAppForPath(pathname, appRegistry)?.displayName ?? DEFAULT_TITLE;
}

/**
 * Custom hook that returns the appropriate page title for the current pathname.
 *
 * @param pathname - The current pathname from useLocation()
 * @param appRegistry - The app registry from PspContext
 * @returns The page title string
 */
export function usePageTitle(pathname: string, appRegistry?: readonly AppConfig[]): string {
  // Memoize the sorted app list to avoid re-sorting on every render
  // Only recalculate when the registry reference changes
  const title = useMemo(() => getPageTitleFromPath(pathname, appRegistry), [pathname, appRegistry]);

  return title;
}
